import { useEffect, useState } from "react";
import { createPolicy, updatePolicyNarratives } from "api/dNet/Policies";
import { convertQuoteToPolicy } from "api/dNet/Quotation";
import { format, isValid } from "date-fns";
import { getActivePolicy, setActivePolicy, getUser, getOldPolicy, setOldPolicy, getPaymentType } from "globals/auth";
import { getSourceType } from "globals/claims";
import { setLoading } from "globals/overlays";
import { setPaymentFormData, getPaymentFormData } from "globals/payments";
import { getQuotation, getActiveQuote } from "globals/renewal";
import { PolicyNetworkLayer } from "helpers";
import {
	isPocaComplianceCheck,
	listMissingDocuments,
	getRenewalRisk,
	getRenewalEndDate,
	removeRatingCodesFromRisks,
	isOnOrBeforeNow,
	getRenewalStartDate
} from "helpers/policy";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { city_county_list_for_limit_group, policy_prefix_list_for_limit_group } from "helpers/policy/constants";
import { clearQuoteCache } from "utils/quoteCache";
import logger from "utils/logger";

export function RenewalHelpersHook() {
	const [error, setError] = useState("");
	const [showErrorModal, setShowErrorModal] = useState(false);
	const activePolicy = useSelector(getActivePolicy);
	const oldPolicy = useSelector(getOldPolicy);
	const sourceType = useSelector(getSourceType);
	const activeQuote = useSelector(getActiveQuote);
	const quotation = useSelector(getQuotation);
	const user = useSelector(getUser);
	const transaction = useSelector(getPaymentFormData);
	const dispatch = useDispatch();
	const paymentType = useSelector(getPaymentType);

	useEffect(() => {
		dispatch(setOldPolicy(activePolicy));
	}, []);

	// Validation function to check if parish and town are valid
	const validateParishAndTown = (parish, town, parishsTowns) => {
		if (!parishsTowns || parishsTowns.length === 0) {
			// If no validation data available, assume valid to avoid breaking existing functionality
			return { isValid: true, parish, town };
		}

		const trimmedParish = parish?.trim();
		const trimmedTown = town?.trim();

		// If no parish provided, consider invalid
		if (!trimmedParish) {
			return { isValid: false, parish: "", town: "" };
		}

		// Find the parish in the validation data
		const parishData = parishsTowns.find(pt => 
			pt.parish?.toLowerCase() === trimmedParish.toLowerCase()
		);

		if (!parishData) {
			// Parish not found in valid list
			return { isValid: false, parish: "", town: "" };
		}

		// If town is provided, validate it against the parish's towns
		if (trimmedTown && parishData.towns) {
			const validTown = parishData.towns.find(t => 
				t?.toLowerCase() === trimmedTown.toLowerCase()
			);
			
			if (!validTown) {
				// Town not valid for this parish, return parish only
				return { isValid: true, parish: trimmedParish, town: "" };
			}
		}

		return { 
			isValid: true, 
			parish: trimmedParish, 
			town: trimmedTown || "" 
		};
	};

	async function createRewrittenPolicy() {
		try {
			// In quote flow, use the encryptedQuoteId from activeQuote
			// In renewal flow, use the quotation_id from quotation object
			const isQuoteFlow = activeQuote?.phase === "quote";
			const quotationId = isQuoteFlow ? activeQuote?.encryptedQuoteId : quotation?.quotation_id;

			if (!quotationId) {
				throw new Error("No quotation ID available for policy conversion");
			}

			const convertQuoteToPolicyResponse = await convertQuoteToPolicy(quotationId, null);

			const createPolicyResponse = await handleCreatePolicy(
				convertQuoteToPolicyResponse.policy_json,
				convertQuoteToPolicyResponse.policy_json.risks
			);

			let newPolicy = {};
			let updatedNewPolicy = {};

			if (createPolicyResponse.success === false)
				throw new Error(createPolicyResponse.message || "Error creating policy");

			newPolicy = await searchForPolicy(
				createPolicyResponse.policy_number,
				activePolicy.risks[0].registration_number,
				activePolicy.risks,
				createPolicyResponse.transaction_id,
				true
			);

			let policyPrefix = convertQuoteToPolicyResponse.policy_json.policy.policy_prefix;
			if (!policyPrefix.includes("MPCTP") && !policyPrefix.includes("MCOTP")) {
				//add valuation report narrative
				let narrativeResult = await PolicyNetworkLayer.addPolicyNarrative(createPolicyResponse.policy_id, ["2032"]);
				if (narrativeResult.success) {
					logger.log("valuation report was successfully added");
				} else {
					logger.log("valuation report was not added");
				}
			}

			// clear local quote cache as purchase completed
			clearQuoteCache();
			updatedNewPolicy = {
				...newPolicy,
				transaction_id: createPolicyResponse.transaction_id,
			};

			const expiryDate = oldPolicy.end_date;

			const cancelPolicyPayload = {
				policy_id: oldPolicy.policy_id,
				effective_date: expiryDate,
				is_manual_premium: true,
				reason: "Policy Re-Issued",
				transaction_premium: 0,
				authorize_transaction: true,
				trans_wording: "Cancelling Policy - CORE- U.",
				external_reference_number: "0",
			};

			await PolicyNetworkLayer.cancelPolicy(cancelPolicyPayload);


			logger.log("New policy created successfully: ", updatedNewPolicy);
			return updatedNewPolicy;
		} catch (e) {
			dispatch(setLoading(false));
			setError(`${e.message}`);
			setShowErrorModal(true);
			throw new Error(`${e.message}`);
		}
	}

	async function handleCreatePolicy(policy_json, risks) {
		// Add activeQuote?.authorizedDriverWording to risks[0]?.authorized_driver_wording
		risks[0].authorized_driver_wording = activeQuote?.authorizedDriverWording;
		let _policy_json = { ...policy_json };
		let _risks = [...risks];
		const legacyGlobals = JSON.parse(JSON.stringify(activePolicy.global_names || []));

		let filteredInsureds = (activePolicy.insureds || []).map((ins, idx) => {
			const legacy = legacyGlobals[idx] || legacyGlobals[0];
			return {
				...ins,
				is_main_insured: ins.is_main_insured,
				global_name_id: legacy.global_name_id,
				global_name_number: legacy.global_name_number,
				national_id: legacy.national_id,
				national_id_type: legacy.national_id_type,
				dob: legacy.dob,
				gender: legacy.gender,
			};
		});

		_policy_json.policy.insureds = filteredInsureds;

		// Fetch parish/town validation data
		let parishsTowns = [];
		try {
			const parishTownsResponse = await PolicyNetworkLayer.getParishTowns();
			if (parishTownsResponse.success) {
				parishsTowns = parishTownsResponse.data || [];
			}
		} catch (error) {
			logger.warn("Failed to fetch parish/town validation data:", error);
			// Continue with empty array - validation will assume valid
		}

		const cleanedGlobals = legacyGlobals.map(g => ({
			...g,
			locations: (g.locations || []).map(loc => {
				const { parish, town, phone_numbers, ...rest } = loc;
			
				const result = {
					...rest,
					country: loc.country?.trim() || "Jamaica",
				};

				// Validate parish and town against backend data
				const validation = validateParishAndTown(parish, town, parishsTowns);
				
				if (validation.isValid) {
					if (validation.parish) {
						result.parish = validation.parish;
					}
					if (validation.town) {
						result.town = validation.town;
					}
				} else {
					// Invalid parish/town combination - log for debugging
					logger.warn(`Invalid parish/town combination found: parish="${parish}", town="${town}". Removing invalid data.`);
				}

				return result;
			}),
		}));

		const newRisksBase = JSON.parse(JSON.stringify(risks || [])); // From quote conversion
		const oldRisksSource = JSON.parse(JSON.stringify(activePolicy.risks || []));

		const finalRisks = oldRisksSource.map((oldRisk, index) => {
			const newRiskData = newRisksBase[index];

			const mergedRisk = { ...oldRisk };

			mergedRisk.authorized_driver_wording = newRiskData.authorized_driver_wording;
			mergedRisk.manual_rates = newRiskData.manual_rates;
			mergedRisk.usage_code = _policy_json.policy.policy_prefix ==="MCOC" || _policy_json.policy.policy_prefix ==="MCOTP" ? 
			"160"  : mergedRisk.usage_code;

			return mergedRisk;
		});

		const mainInsured = legacyGlobals.find(
			g => g.global_name_id === filteredInsureds.find(i => i.is_main_insured === true)?.global_name_id
		);
	const location = mainInsured?.locations?.find(l => l.is_main_location === true) || {};
		const generalArea = location.general_area?.toLowerCase() || "";

		logger.log("main location", location);

		const reducedLimitsAreAvailable =
			policy_prefix_list_for_limit_group.includes(_policy_json.policy.policy_prefix) &&
			location.country === "Jamaica" &&
			location.parish === "St. Catherine" &&
			city_county_list_for_limit_group.some(area => {
				let shouldReduceLimits = false;

				if (
					generalArea.toLowerCase().includes(area.toLowerCase()) ||
					location.town?.toLowerCase().includes(area.toLowerCase())
				)
					shouldReduceLimits = true;

				return shouldReduceLimits;
			});

		logger.log("reducedLimitsAreAvailable", reducedLimitsAreAvailable);

		let _policy = { ..._policy_json.policy };
		let _manual_extensions = [..._policy.manual_extensions];
		let _manual_limits = [..._policy.manual_limits];
		let _limit_group = reducedLimitsAreAvailable ? "Reduced Limits" : "Standard Limits";

		//format manual extensions
		for (let i = 0; i < _manual_extensions.length; i += 1) {
			_manual_extensions[i] = {
				..._manual_extensions[i],
				limit_amount:
					_manual_extensions[i].limit_amount === undefined ? `0` : _manual_extensions[i].limit_amount.toString(),
			};
		}
		const startDate = _policy_json.policy.start_date;
		const endDate = getRenewalEndDate(startDate) || "";
		const renewalRisks = getRenewalRisk(activePolicy.risks) || [];

		_policy = {
			..._policy,
			start_date: startDate,
			end_date: endDate,
			manual_extensions: _manual_extensions,
			extensions: _manual_extensions,
			limit_group: _limit_group,
			manual_limits: reducedLimitsAreAvailable ? [] : _manual_limits,
			payment_plan_id: paymentType.paymentPlanId,
			paymemt_plan_id: paymentType.paymentPlanId,
		};
		const effectiveCover =
			activePolicy.type_of_cover && activePolicy.type_of_cover.toLowerCase().includes("passenger vehicle") ? 30 : 7;

		// Validate startDate before using it
		const startDateObj = new Date(startDate);
		if (!isValid(startDateObj)) {
			logger.error("Invalid startDate derived:", startDate);
			// Handle the error - throw an error or use a default? Throwing might be safer here.
			throw new Error("Could not determine a valid start date for the policy.");
		}

		const isPocaCompliant = isPocaComplianceCheck(format(startDateObj, "MM/dd/yyyy"), user.poca);
		const noPendingDocuments = listMissingDocuments(activePolicy.renewal_narratives).length === 0;

		const coverNoteDetails = {
  days_effective: effectiveCover,
};

if (isOnOrBeforeNow(_policy.start_date)) {
  coverNoteDetails.effective_date = _policy.start_date;
}
		_policy_json = {
			..._policy_json,
			authorize_transaction: true,
			create_certificate:  noPendingDocuments,
			create_cover_note: true,
			cover_note_details: coverNoteDetails,
			policy: _policy,
			global_names: cleanedGlobals,
			risks: finalRisks,
			payment_plan_id: paymentType.paymentPlanId,
		};

		var newPol = {
			resquestkey: {},
			..._policy_json,
		};

		const policyCreated = await createPolicy(newPol);

		return policyCreated;
	}

	async function searchForPolicy(policyNo, licenseNo, _risks, transactionId, shouldReturnPolicy = false) {
		setLoading(true);
		let isFound = false; //Variable if policy is found
		try {
			let getPolicyResponse = await PolicyNetworkLayer.getPolicyForUW(policyNo, licenseNo);

			if (Object.keys(getPolicyResponse).length === 0 || getPolicyResponse?.Info) {
				throw new Error("Error fetching new policy");
			} else {
				if (getPolicyResponse.success) {
					let _newPolicy = { ...getPolicyResponse.policy };

					let _newRisks = [...getPolicyResponse.risks];

					let temp_risks;

					const ratingCodeToRemove = 399;

					if (sourceType === "Broker") _newRisks = removeRatingCodesFromRisks(_newRisks, [ratingCodeToRemove]);

					for (let i = 0; i < _risks?.length; i += 1) {
						temp_risks = {
							..._newRisks[i],
							discounts: [..._risks[i].discounts],
						};
						_newRisks[i] = { ...temp_risks };
					}

					const newPolicy = {
						..._newPolicy,
						risks: _newRisks,
					};

					dispatch(setActivePolicy(newPolicy));

					const stampDuty = activeQuote?.premium_calculation?.stamp_duty ?? activeQuote?.stampDuty ?? 0;

					let paymentTotal = Number.parseFloat(transaction.paymentAmount) + Number.parseFloat(stampDuty);
					dispatch(
						setPaymentFormData({
							...transaction,
							transactionId: transactionId,
							policyNumber: newPolicy.policy_number,
							paymentAmount: `${paymentTotal}`,
						})
					);

					isFound = true; //Set to true if request is successfull

					if (shouldReturnPolicy && isFound) return newPolicy;
				} else {
					setLoading(false);
					isFound = false; //Set to false if request is unsuccessfull
				}
			}
		} catch (err) {
			setShowErrorModal(true);
			setLoading(false);
			isFound = false;
		} finally {
			setLoading(false);
		}

		return isFound;
	}

	const isValidDate = date => {
		if (date instanceof Date && !isNaN(date.getTime())) {
			return true;
		}
		if (typeof date === "string" || typeof date === "number") {
			const timestamp = Date.parse(date.toString());
			return !isNaN(timestamp);
		}
		return false;
	};

	const formatDateForPolicyCreate = (date, isRenewal) => {
		let safeDate;
		if (date instanceof Date) {
			safeDate = date;
		} else {
			safeDate = new Date(date);
		}

		if (!isValidDate(safeDate)) {
			safeDate = new Date();
		}

		const formatdate = new Intl.DateTimeFormat("en-US", {
			month: "2-digit",
			day: "2-digit",
			year: "numeric",
		}).format(safeDate);

		let formattime = new Intl.DateTimeFormat("en-US", {
			hour: "numeric",
			minute: "numeric",
			second: "numeric",
			hour12: false,
		}).format(safeDate);

		formattime = isRenewal ? "00:01:00" : formattime;

		return formatdate + " " + formattime;
	};
	return {
		createRewrittenPolicy,
	};
}
